import React, { useState, useEffect } from 'react';
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Snackbar,
} from '@mui/material';
import {
  FiberManualRecord as OnlineIcon,
  Schedule as AwayIcon,
  DoNotDisturb as BusyIcon,
  Restaurant as LunchIcon,
  Coffee as BreakIcon,
  MeetingRoom as MeetingIcon,
  RadioButtonUnchecked as OfflineIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import userPresenceService from '../../services/userPresence.service';

const statusConfig = {
  available: { icon: <OnlineIcon />, color: '#4caf50', label: 'Available' },
  away: { icon: <AwayIcon />, color: '#ff9800', label: 'Away' },
  busy: { icon: <BusyIcon />, color: '#f44336', label: 'Busy' },
  lunch: { icon: <LunchIcon />, color: '#ff5722', label: 'At Lunch' },
  break: { icon: <BreakIcon />, color: '#795548', label: 'On Break' },
  meeting: { icon: <MeetingIcon />, color: '#9c27b0', label: 'In Meeting' },
  offline: { icon: <OfflineIcon />, color: '#9e9e9e', label: 'Offline' }
};

const PresenceSelector: React.FC = () => {
  const { member } = useAuth();
  const [currentStatus, setCurrentStatus] = useState<string>('available');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch current presence status
  useEffect(() => {
    const fetchPresence = async () => {
      try {
        const response = await userPresenceService.getPresence();
        if (response.success && response.data.currentUser) {
          setCurrentStatus(response.data.currentUser.status || 'available');
        }
      } catch (error) {
        console.error('Error fetching presence:', error);
      }
    };

    fetchPresence();
  }, []);

  const handleStatusChange = async (newStatus: string) => {
    try {
      setLoading(true);
      const response = await userPresenceService.updateStatus(newStatus);
      
      if (response.success) {
        setCurrentStatus(newStatus);
        setSnackbar({
          open: true,
          message: 'Status updated successfully',
          severity: 'success'
        });
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update status',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const config = statusConfig[currentStatus as keyof typeof statusConfig] || statusConfig.offline;

  return (
    <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {member?.extension ? `Ext: ${member.extension} - Your Status` : 'Your Status'}
      </Typography>
      <FormControl size="small" fullWidth>
        <Select
          value={currentStatus}
          onChange={(e) => handleStatusChange(e.target.value)}
          disabled={loading}
          displayEmpty
          sx={{
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.75,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: config.color,
              borderWidth: 2,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: config.color,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: config.color,
            },
          }}
        >
          {Object.entries(statusConfig).map(([key, config]) => (
            <MenuItem key={key} value={key}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ color: config.color, display: 'flex', alignItems: 'center' }}>
                  {config.icon}
                </Box>
                <Typography variant="body2">{config.label}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default PresenceSelector;